<template>
	<Layout>
		<ModalFollowSearch
			:modal-name="modalNameFollowSearch"
			@close="handleCloseFollowSearchModal"
		/>
		<template v-if="!isMobileViewport">
			<JumbotronCarousel
				v-if="slides.length > 0"
				:slides="slides"
			>
				<template #after>
					<HeaderSponsors
						v-if="headerSponsors"
						:sponsors="headerSponsors"
					/>
					<FeedNavigationDesktop />
				</template>
			</JumbotronCarousel>
			<Jumbotron
				v-else
				:bg-image-url="bgImageUrl"
				:main-image-url="mainImageUrl"
				:title="title"
				:special-announcement="false"
			>
				<template #after>
					<HeaderSponsors
						v-if="headerSponsors"
						:sponsors="headerSponsors"
					/>
					<FeedNavigationDesktop />
				</template>
			</Jumbotron>
		</template>
		<div class="feed l-container l-container--full-width@mobile">
			<div class="l-row">
				<div
					class="l-col l-col--2@md feed__filter-container"
				>
					<PillFilter
						v-if="filterOptions.length > 1"
						class="feed__filter"
						:options="filterOptions"
						:value="filterValue"
						:multi="false"
						:orientation="isMobileViewport ? 'horizontal' : 'vertical'"
						@change="onFilterChange"
					/>
				</div>
				<div class="l-col l-col--8@md">
					<LoadingSpinner
						v-if="isLoading || !isInitialized"
						class="feed__loading-spinner"
					/>
					<FollowingEmptyState
						v-else-if="videos.items.length === 0 && isLoggedIn"
						:type="filterValue === 'following' ? 'following' : 'saved'"
						@open-follow="openFollowSearchModal"
					/>
					<div
						class="feed__cards"
						:class="{
							'-loading': isLoading,
						}"
					>
						<template v-for="(event, i) in videos.items">
							<EventCard
								:key="`${event.id}-${event.isBookmarked}`"
								v-bind="EventCardProps(event, {
									showEventTags: $arenaConfig.showEventCardTags,
									hidePaymentRestriction:	hasAccessToEvent(event),
									hasBookmarkedEvent:	hasBookmarkedEvent(event),
									flexibleSize: true,
								})"
								:data-event-id="event.id"
								:enable-video-preview="true"
								:is-feed-card="true"
								:sharing-enabled="supportsNativeShare && isTouchDevice"
								:is-in-viewport="activePreviewCardId === event.id.toString()"
								:bookmark-enabled="isLoggedIn"
								@share="handleShare"
								@save="handleSave"
								@unsave="handleUnsave"
							/>
							<FollowingEmptyState
								v-if="i === 1 && filterValue === 'following'"
								:key="`following-empty-state-${i}`"
								class="feed__following-empty-state"
								type="following"
								@open-follow="openFollowSearchModal"
							/>
						</template>
					</div>
					<div
						v-if="videos.pagination.currentPage < videos.pagination.lastPage"
						ref="loadMoreTrigger"
						class="feed__load-more-trigger"
					/>
				</div>
			</div>
		</div>
	</Layout>
</template>

<script>
import Layout from '@js/Views/Layout';
import Jumbotron from '@js/components/Jumbotron';
import JumbotronCarousel from '@js/components/JumbotronCarousel';
import HeaderSponsors from '@js/components/HeaderSponsors';
import LoadingSpinner from '@shared/components/LoadingSpinner';
import PillFilter from '@shared/components/PillFilter';
import EventCard from '@shared/components/EventCard/EventCard';
import ModalFollowSearch from '@js/Pages/FanAccount/ModalFollowSearch';
import FollowingEmptyState from '@js/Pages/FanAccount/partials/FollowingEmptyState';
import FeedNavigationDesktop from '@js/components/FeedNavigationDesktop';
import { EventCardProps } from '@shared/components/EventCard/EventCardProps';
import { AdType } from '@shared/enums/AdType';
import { mapGetters, mapActions } from 'vuex';
import { throttle } from '@shared/utils/throttle';
import laroute from '@laroute';

export default {
	name:       'ViewFeed',
	components: {
		Layout,
		Jumbotron,
		JumbotronCarousel,
		HeaderSponsors,
		LoadingSpinner,
		EventCard,
		PillFilter,
		FollowingEmptyState,
		ModalFollowSearch,
		FeedNavigationDesktop,
	},
	props: {
		bgImageUrl: {
			type:    String,
			default: null,
		},
		mainImageUrl: {
			type:    String,
			default: null,
		},
		title: {
			type:    String,
			default: null,
		},
		slides: {
			type:    Array,
			default: () => [],
		},
		ads: {
			type:    Array,
			default: () => [],
		},
	},
	data() {
		return {
			EventCardProps,
			videos:                this.getInitialVideos(),
			isInitialized:         false,
			isLoading:             false,
			loadMoreObserver:      null,
			previewObserver:       null,
			filterValue:           null,
			modalNameFollowSearch: 'followSearchModal',
			activePreviewCardId:   null,
		};
	},
	computed: {
		...mapGetters({
			isMobileViewport:     'device/isMobileViewport',
			isTouchDevice:        'device/isTouchDevice',
			supportsNativeShare:  'device/supportsNativeShare',
			isAthlete:            'user/isAthlete',
			userId:               'user/userId',
			queryKey:             'user/queryKey',
			profileIsInitialized: 'user/isInitialized',
			hasAccessToEvent:     'user/hasAccessToEvent',
			hasBookmarkedEvent:   'user/hasBookmarkedEvent',
			isLoggedIn:           'user/isLoggedIn',
		}),
		headerSponsors() {
			return this.ads.find(ad => ad.type === AdType.SPONSORS_HEADER)?.logos;
		},
		filterOptions() {
			const options = [{ label: this.$I18n.trans_choice('commons.recommendation', 2), value: 'recommended' }];

			if (this.isLoggedIn) {
				options.push({ label: this.$I18n.trans('arena.feed.filter.following'), value: 'following' });
				if (this.isAthlete) {
					options.push({ label: this.$I18n.trans('arena.feed.filter.my_videos'), value: 'my_videos' });
				}
				options.push({ label: this.$I18n.trans('arena.feed.filter.saved'), value: 'saved' });
			}

			return options;
		},
	},
	watch: {
		async profileIsInitialized() {
			this.filterValue = this.filterOptions[0].value;

			if (this.filterOptions?.length > 0) {
				await this.getVideos();
			}
			this.isInitialized = true;
		},
	},
	mounted() {
		// Add scroll listener to check if active video should stop playing
		if (this.isTouchDevice) {
			window.addEventListener('scroll', this.checkActiveVideoPosition);
		}
	},
	beforeDestroy() {
		if (this.loadMoreObserver) {
			this.loadMoreObserver.disconnect();
		}
		if (this.previewObserver) {
			this.previewObserver.disconnect();
		}
		// Remove scroll listener
		if (this.isTouchDevice) {
			window.removeEventListener('scroll', this.checkActiveVideoPosition);
		}
	},
	methods: {
		...mapActions({
			shareNative:     'device/shareNative',
			loadProfile:     'user/loadProfile',
			bookmarkEvent:   'user/bookmarkEvent',
			unbookmarkEvent: 'user/unbookmarkEvent',
		}),
		getInitialVideos() {
			return {
				items:      [],
				pagination: {
					currentPage: 1,
					perPage:     10,
					lastPage:    1,
					total:       0,
				},
			};
		},
		async fetchVideos(page = 1, append = false) {
			if (this.isLoading) return;

			this.isLoading = true;
			try {
				const searchParams = new URLSearchParams();
				searchParams.append('page', JSON.stringify(page));
				searchParams.append('perPage', JSON.stringify(this.videos.pagination.perPage));
				const queryFilter = { [this.filterValue]: this.queryKey ?? -1 };
				searchParams.append('filters', JSON.stringify(queryFilter));

				const url = `${laroute.route('api.events.cached')}?${searchParams}`;

				const response = await fetch(url);
				if (!response.ok) {
					throw new Error(`HTTP error! status: ${response.status}`);
				}
				const data = await response.json();

				// Store the last item's position before adding new items
				const lastItem = append ? this.videos.items[this.videos.items.length - 1] : null;
				const lastItemPosition = lastItem ? document.querySelector(`[data-event-id="${lastItem.id}"]`)?.getBoundingClientRect().top : null;

				if (append) {
					this.videos.items = [...this.videos.items, ...data.data];
				} else {
					this.videos.items = data.data;
				}
				this.videos.pagination = data.meta;

				// Restore scroll position after new items are added
				if (append && lastItemPosition !== null) {
					this.$nextTick(() => {
						const newLastItem = document.querySelector(`[data-event-id="${lastItem.id}"]`);
						if (newLastItem) {
							const newPosition = newLastItem.getBoundingClientRect().top;
							window.scrollBy(0, newPosition - lastItemPosition);
						}
					});
				}

				// Set up observers after initial load or after appending new items
				this.$nextTick(() => {
					this.setupIntersectionObserver();
					this.setupPreviewObserver();
				});
			} catch (error) {
				console.error('Error fetching videos:', error);
				this.videos = this.getInitialVideos();
			} finally {
				this.isLoading = false;
			}
		},
		setupIntersectionObserver() {
			// Clean up existing observer if it exists
			if (this.loadMoreObserver) {
				this.loadMoreObserver.disconnect();
			}

			this.loadMoreObserver = new IntersectionObserver(
				(entries) => {
					const isIntersecting = entries[0].isIntersecting;

					if (isIntersecting && !this.isLoading && this.videos.pagination.currentPage < this.videos.pagination.lastPage) {
						this.loadMoreVideos();
					}
				},
				{
					rootMargin: '200px',
					threshold:  0.1,
				}
			);

			if (this.$refs.loadMoreTrigger) {
				this.loadMoreObserver.observe(this.$refs.loadMoreTrigger);
			}
		},
		setupPreviewObserver() {
			// Only setup preview observer for touch devices
			if (!this.isTouchDevice) {
				return;
			}

			// Create throttled version of updateActiveVideo to prevent rapid-fire API requests
			this.throttledUpdateActiveVideo = throttle(this.updateActiveVideo, 500);

			// Clean up existing preview observer if it exists
			if (this.previewObserver) {
				this.previewObserver.disconnect();
			}

			this.previewObserver = new IntersectionObserver(
				() => {
					// Use throttled version to prevent excessive API requests during fast scrolling
					this.throttledUpdateActiveVideo();
				},
				{
					rootMargin: '-80px 0px -80px 0px', // 80px margin from top and bottom
					threshold:  [0, 0.1, 0.5, 0.9, 1.0], // Multiple thresholds for better detection
				}
			);

			// Observe all event cards
			this.$nextTick(() => {
				const eventCardElements = document.querySelectorAll('[data-event-id]');
				eventCardElements.forEach((element) => {
					this.previewObserver.observe(element);
				});
			});
		},
		updateActiveVideo() {
			// Get all event card elements
			const eventCardElements = document.querySelectorAll('[data-event-id]');
			const qualifyingCards = [];

			// Find all cards that are fully visible with 80px margins from top and bottom
			eventCardElements.forEach((element) => {
				const rect = element.getBoundingClientRect();
				const isFullyVisible = rect.top >= 80 && rect.bottom <= window.innerHeight - 80;

				if (isFullyVisible) {
					qualifyingCards.push({
						element,
						rect,
						id: element.getAttribute('data-event-id'),
					});
				}
			});

			// Sort by top position (topmost first) and take the first one
			qualifyingCards.sort((a, b) => a.rect.top - b.rect.top);
			const newActiveId = qualifyingCards.length > 0 ? qualifyingCards[0].id : null;

			// Only update if it's different to avoid unnecessary reactivity
			if (this.activePreviewCardId !== newActiveId) {
				this.activePreviewCardId = newActiveId;
			}
		},
		async getVideos() {
			await this.fetchVideos(1, false);
		},
		async loadMoreVideos() {
			await this.fetchVideos(this.videos.pagination.currentPage + 1, true);
		},
		onFilterChange(value) {
			this.filterValue = value;
			this.getVideos();
		},
		openFollowSearchModal() {
			this.eventBus.$emit(`modal:${this.modalNameFollowSearch}`);
		},
		handleCloseFollowSearchModal() {
			this.getVideos();
		},
		checkActiveVideoPosition() {
			// If there's no active video, nothing to check
			if (!this.activePreviewCardId) {
				return;
			}

			// Find the currently active video element
			const activeElement = document.querySelector(`[data-event-id="${this.activePreviewCardId}"]`);
			if (!activeElement) {
				return;
			}

			// Check if the active video is still fully visible with 80px margins from top and bottom
			const rect = activeElement.getBoundingClientRect();
			const isStillFullyVisible = rect.top >= 80 && rect.bottom <= window.innerHeight - 80;

			// If the active video is no longer fully visible, stop it and find a new one
			if (!isStillFullyVisible) {
				this.updateActiveVideo();
			}
		},
		handleShare(event) {
			this.shareNative({
				text: event.name,
				url:  event.url,
			});
		},
		async handleSave(event) {
			await this.bookmarkEvent({ event });
		},
		async handleUnsave(event) {
			await this.unbookmarkEvent({ event });
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@sass/variables';

.feed {
	position: relative;
	margin-top: $sp5;

	@include size(md-up) {
		margin-top: $sp7;
		margin-bottom: $sp7;
	}
}

.feed__filter-container {
	position: sticky;
	top: 0;
	z-index: 3; // stylelint-disable-line

	&::after {
		content: '';
		position: absolute;
		top: 0;
		left: 50%;
		width: 100vw;
		height: 100%;
		background-color: var(--color-body-background);
		transform: translateX(-50%);
		z-index: -1;
	}

	@include size(md-up) {
		&::after {
			display: none;
		}
	}
}

.feed__filter {
	position: sticky;
	top: $sp5;
	margin-bottom: $sp5;
	padding-left: $sp3;
	padding-right: $sp3;
	padding-top: $sp5;

	@include size(sm-up) {
		padding-left: 0;
		padding-right: 0;
	}

	@include size(md-up) {
		padding-top: 0;
	}
}

.feed__cards {
	transition: opacity $trans-time-fast;
	opacity: 1;

	&.-loading {
		opacity: 0.5;
	}
}

.feed__loading-spinner {
	position: absolute;
	display: flex;
	align-items: center;
	top: 50%;
	left: 50%;
	transform: translateX(-50%) translateY(-50%);
	margin-bottom: $sp5;
}

.feed__load-more-trigger {
	height: 1px;
	width: 100%;
}

.feed__following-empty-state {
	padding-top: 0;
	border-top: none;
	border-bottom: none;
}
</style>
